// Components
import Button from '@components/Button/Button';
import EmailSignatureEditorPreview from '@components/EmailSignaturev2/EmailSignatureEdit/EmailSignatureEditor/EmailSignatureEditorPreview';
import SideEditorCoreServicesProvider from '@components/EmailSignaturev2/EmailSignatureEdit/EmailSignatureEditor/SideEditorCoreServices';
import type EmailSignatureImagesUploader from '@components/EmailSignaturev2/EmailSignatureEdit/EmailSignatureImagesUploader';
import EmailSignatureLoadingVeneer from '@components/EmailSignaturev2/EmailSignatureEdit/EmailSignatureLoadingVeneer';
import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
import { FocusLayoutSideEditorContainer } from '@components/Layout/FocusLayout';
import { TwoSidedOnboardingLayout } from '@components/OnboardingLayout/OnboardingLayout';
import { SideEditor, SideEditorPanels } from '@components/SideEditor/SideEditor';
// hooks and context
import { sideEditorContext } from '@context/side-editor-context';
import { upgradeFlowContext } from '@context/upgrade-flow-context';
import { analytics } from '@modules/analytics';
import { isObjectActuallyEmpty } from '@modules/email-signatures-helpers';
// types
import type { TabsOrientation } from '@reach/tabs';
import type { EmailSignaturesActions } from '@reducers/email-signature-reducer';
// utils
// es-toolkit/compat: lodash isEmpty treats 0/true/null as empty
import { isEmpty } from 'es-toolkit/compat';
import { useRouter } from 'next/router';
import { type Dispatch, type FC, useCallback, useMemo, useState } from 'react';
import type { AnonymousUser } from 'types';

import type { EmailSignatureFieldTypes } from '../../consts';
import EmailSignatureErrorUploadingImagesDialog, {
  getErrorUploadingMessage,
} from '../../EmailSignatureDialogs/EmailSignatureErrorUploadingImagesDialog';
import EmailSignatureEditorAddonsPanel from '../../EmailSignatureSideEditor/Addons/EmailSignatureEditorAddonsPanel';
import EmailSignatureEditorDesignPanel from '../../EmailSignatureSideEditor/Design/EmailSignatureEditorDesignPanel';
import EmailSignatureEditorDetailsPanel from '../../EmailSignatureSideEditor/Details/EmailSignatureEditorDetailsPanel';
import EmailSignatureEditorImagesPanel from '../../EmailSignatureSideEditor/Images/EmailSignatureEditorImagesPanel';
import EmailSignatureEditorSocialPanel from '../../EmailSignatureSideEditor/Social/EmailSignatureEditorSocialPanel';
import { resetWhenSomethingGoneWrong } from '../utils';
import OnboardingEditorMenu from './OnboardingEditorMenu';
import styles from './OnboardingEmailSignatureEditor.module.scss';
import { OnboardingHelpDialog } from './OnboardingHelpDialog';

type OnboardingEmailSignatureEditorProps = {
  onboardingEmailSignature: EmailSignatureV2;
  onboardingEmailSignatureDispatch: Dispatch<EmailSignaturesActions>;
  onboardingEmailSignatureHasLoaded: boolean;
  handleSave: (
    emailSignature: EmailSignatureV2,
    onComplete: (savedEmailSignature: EmailSignatureV2, savedUser: AnonymousUser) => void,
  ) => Promise<void>;
  uploadEmailSignatureFieldImagesThunk: (
    uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void,
  ) => void;
  isEditingSavedEmailSignature: boolean;
};

/*
    Email signature editor for onboarding flow
*/
const OnboardingEmailSignatureEditor: FC<
  React.PropsWithChildren<OnboardingEmailSignatureEditorProps>
> = ({
  handleSave,
  uploadEmailSignatureFieldImagesThunk,
  onboardingEmailSignature,
  onboardingEmailSignatureDispatch,
  onboardingEmailSignatureHasLoaded,
  isEditingSavedEmailSignature,
}) => {
  const router = useRouter();
  const [openErrorUploadingWarning, setOpenErrorUploadingWarning] = useState<boolean>(false);
  const [hasOpenedErrorUploading, setHasOpenedErrorUploading] = useState<boolean>(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [fieldTypeOfTheFailedUploadingImage, setFieldTypeOfTheFailedUploadingImage] =
    useState<EmailSignatureFieldTypes>();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);

  function changeTab(newTab: number) {
    setActiveTab(newTab);
  }

  const isEverythingEmpty = useMemo(() => {
    return (
      isObjectActuallyEmpty(onboardingEmailSignature.personalDetails) &&
      isEmpty(onboardingEmailSignature.contactDetails) &&
      isEmpty(onboardingEmailSignature.socialLinks) &&
      isEmpty(onboardingEmailSignature.images.filter((image) => image.imageUrl)) &&
      !onboardingEmailSignature.environmentalFooter &&
      !onboardingEmailSignature.legalDisclaimer &&
      !onboardingEmailSignature.banner
    );
  }, [
    onboardingEmailSignature.personalDetails,
    onboardingEmailSignature.contactDetails,
    onboardingEmailSignature.socialLinks,
    onboardingEmailSignature.images,
    onboardingEmailSignature.environmentalFooter,
    onboardingEmailSignature.legalDisclaimer,
    onboardingEmailSignature.banner,
  ]);

  const handleOnErrorUploading = useCallback(
    (fieldType: EmailSignatureFieldTypes) => {
      if (!hasOpenedErrorUploading) {
        setHasOpenedErrorUploading(true);
        setOpenErrorUploadingWarning(true);
        setFieldTypeOfTheFailedUploadingImage(fieldType);
      }
    },
    [hasOpenedErrorUploading],
  );

  async function handleSaveEmailSignature() {
    setIsSaving(true);
    await handleSave(onboardingEmailSignature, (_, savedUser) => {
      setIsSaving(false);
      if (savedUser.isAnonymous) {
        router.push('/onboarding/email-signature/signup');
      } else {
        router.push('/onboarding/email-signature/setup');
      }
    });
  }

  return (
    <upgradeFlowContext.Provider value={{ flowType: 'onboarding' }}>
      <TwoSidedOnboardingLayout title={'Create your first email signature'}>
        <FocusLayoutSideEditorContainer>
          <SideEditorCoreServicesProvider
            emailSignature={onboardingEmailSignature}
            emailSignatureDispatch={onboardingEmailSignatureDispatch}
            emailSignatureHasBeenLoaded={onboardingEmailSignatureHasLoaded}
            handleOnErrorUploading={handleOnErrorUploading}
            uploadEmailSignatureFieldImagesThunk={uploadEmailSignatureFieldImagesThunk}
            isEverythingEmpty={isEverythingEmpty}
            isEditingSavedEmailSignature={isEditingSavedEmailSignature}
          >
            <sideEditorContext.Provider
              value={{
                orientation: 'vertical' as TabsOrientation,
                activeTab,
                changeTab,
              }}
            >
              <SideEditor className={styles['em-sig-editor']} data-tab={activeTab}>
                <OnboardingEditorMenu
                  onHelpClick={() => {
                    analytics.track({
                      name: 'onboarding_signature_helpbutton_clicked',
                    });
                    setShowHelpDialog(true);
                  }}
                  isTeamEmailSignature={onboardingEmailSignature.isTeamEmailSignature}
                  emailSignatureHasBeenLoaded={onboardingEmailSignatureHasLoaded}
                />

                <SideEditorPanels className={styles['em-sig-editor-panels']}>
                  <EmailSignatureLoadingVeneer isVisible={!onboardingEmailSignatureHasLoaded} />
                  <EmailSignatureEditorDetailsPanel />
                  <EmailSignatureEditorImagesPanel />
                  <EmailSignatureEditorSocialPanel />
                  <EmailSignatureEditorDesignPanel trackCustomColor={true} />
                  <EmailSignatureEditorAddonsPanel />
                </SideEditorPanels>
              </SideEditor>
              <OnboardingHelpDialog show={showHelpDialog} handleChangeShow={setShowHelpDialog} />
            </sideEditorContext.Provider>
          </SideEditorCoreServicesProvider>
        </FocusLayoutSideEditorContainer>

        <div className={styles['em-sig-preview-section']}>
          <div className={styles['em-sig-preview-container']}>
            <EmailSignatureEditorPreview
              isEverythingEmpty={isEverythingEmpty}
              emailSignature={onboardingEmailSignature}
              emailSignatureHasBeenLoaded={onboardingEmailSignatureHasLoaded}
            />
            <div className={styles['em-sig-submission-container']}>
              <span className={styles['em-sig-submission-buttons-container']}>
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleSaveEmailSignature}
                  disabled={isEverythingEmpty || !onboardingEmailSignatureHasLoaded}
                  loading={isSaving}
                  className={styles['em-sig-button']}
                  isFullWidth={false}
                >
                  All Done!
                </Button>
              </span>
            </div>
          </div>
        </div>

        <EmailSignatureErrorUploadingImagesDialog
          showErrorDialog={openErrorUploadingWarning}
          closeDialog={async () => {
            setOpenErrorUploadingWarning(false);
            await resetWhenSomethingGoneWrong(router);
          }}
        >
          {getErrorUploadingMessage(fieldTypeOfTheFailedUploadingImage)}
        </EmailSignatureErrorUploadingImagesDialog>
      </TwoSidedOnboardingLayout>
    </upgradeFlowContext.Provider>
  );
};

export { OnboardingEmailSignatureEditor };
